import { useMutation, useQuery } from '@tanstack/react-query';

import { insightsApi } from './api/insights.api';
import type { ExportFormat, VarianceFilters } from './types';

export const insightsKeys = {
  dashboard: ['dashboard'] as const,
  workload: ['team-workload'] as const,
  portfolio: ['portfolio'] as const,
  variance: (f: VarianceFilters) => ['variance', f] as const,
  analytics: (weeks: number) => ['analytics', weeks] as const,
  departments: (days: number) => ['insights', 'departments', days] as const,
  hours: (userId: string, from: string, to: string) => ['insights', 'hours', userId, from, to] as const
};

export function useDashboard(sprintId?: string) {
  return useQuery({ queryKey: [...insightsKeys.dashboard, sprintId ?? 'active'], queryFn: () => insightsApi.dashboard(sprintId), refetchInterval: 60_000 });
}

/** Sprint progress shared by the sidebar and the dashboard header. */
export function useSprintProgress() {
  const q = useDashboard();
  return { sprint: q.data?.sprint ?? null, isPending: q.isPending };
}

export function useTeamWorkload() {
  return useQuery({ queryKey: insightsKeys.workload, queryFn: insightsApi.teamWorkload });
}

export function usePortfolio() {
  return useQuery({ queryKey: insightsKeys.portfolio, queryFn: insightsApi.portfolio });
}

export function useVarianceReport(filters: VarianceFilters) {
  return useQuery({ queryKey: insightsKeys.variance(filters), queryFn: () => insightsApi.variance(filters), placeholderData: (prev) => prev });
}

export function useAnalytics(weeks = 8) {
  return useQuery({ queryKey: insightsKeys.analytics(weeks), queryFn: () => insightsApi.analytics(weeks) });
}

/** Downloads an export through the authenticated client (a plain link would not carry the bearer). */
export function useExportVariance() {
  return useMutation({
    mutationFn: async ({ filters, format }: { filters: VarianceFilters; format: ExportFormat }) => {
      const { blob, filename } = await insightsApi.exportVariance(filters, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return filename;
    }
  });
}

export function useDepartmentStats(days = 30) {
  return useQuery({ queryKey: insightsKeys.departments(days), queryFn: () => insightsApi.departments(days) });
}

export function useHoursReport(userId: string, from: string, to: string) {
  return useQuery({ queryKey: insightsKeys.hours(userId, from, to), queryFn: () => insightsApi.hoursReport(userId, from, to), enabled: !!userId && !!from && !!to });
}
